import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { networksAPI } from 'api';
import { RejectWithMessage, TableDataResponse } from 'features/types';
import { Paging, Tab } from 'api/types';
import { getErrorMessage } from 'utils/error';
import { RootState } from 'app/root-reducer';
import { GetJoinNetworksTable, GetJoinNetworksTableFilters, JoinNetworkData, JoinNetworkTableView, NetworkJoinState, JoinNetworkTableTabs } from './types';
import { NetworkUserType } from '../../pages/job-seeker-program-networks/components/Networks/types'

export const getJoinTable = createAsyncThunk<TableDataResponse<JoinNetworkTableView>, NetworkUserType, RejectWithMessage>(
  'network-join/get-join-table',
  async (userType, { rejectWithValue, getState }) => {
    try {
      const state: RootState = getState();
      const requestData = { ...state.networkJoin.requestData };
      const onBehalfOfOrganization = state.jobSeekerOnBehalfOfOrganization.onBehalfOfOrganization;
      if (onBehalfOfOrganization) {
        requestData.onBehalfOfOrganizationId = onBehalfOfOrganization?.value;
      }

      return userType === NetworkUserType.User ?
        await networksAPI.getJoinTable(requestData) :
        await networksAPI.getJoinTableForLeadJobSeeker(requestData);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const getJoinTableForLeadJobSeekerTabs = createAsyncThunk<Tab[], void, RejectWithMessage>(
  'network-join/get-join-tabs',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state: RootState = getState();
      const requestData = { ...state.networkJoin.requestData };
      const onBehalfOfOrganization = state.jobSeekerOnBehalfOfOrganization.onBehalfOfOrganization;
      if (onBehalfOfOrganization) {
        requestData.onBehalfOfOrganizationId = onBehalfOfOrganization?.value;
      }
      return networksAPI.getJoinTableForLeadJobSeekerTabsData(requestData);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const getJoinData = createAsyncThunk<JoinNetworkData, { networkId: string; userType: NetworkUserType; }, RejectWithMessage>(
  'network-join/get-join-data',
  async ({ networkId, userType }, { rejectWithValue, getState }) => {
    try {
      return userType === NetworkUserType.User ?
        await networksAPI.getJoinData(networkId) :
        await networksAPI.getJoinDataForLeadJobSeeker(networkId);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

const initialRequestData: GetJoinNetworksTable = {
  filters: {
    tab: JoinNetworkTableTabs.Discover
  },
  paging: {
    page: 1,
    pageSize: 20,
  },
};

const initialState: NetworkJoinState = {
  requestData: initialRequestData,
  data: {
    tableDataIsLoading: false,
    tabsIsLoading: false,
    tabs: [],
    tableData: {
      rows: [],
      totalCount: 0,
      noResult: false,
    },
    joinDataIsLoading: false,
    joinData: null,
  },
};

const networkJoinSlice = createSlice({
  name: 'network-join',
  initialState,
  reducers: {
    resetRequestData: (state) => {
      state.requestData = { ...initialState.requestData };
    },
    setPage: (state, action: PayloadAction<Paging>) => {
      state.requestData!.paging.page = action.payload.page;
      state.requestData!.paging.pageSize = action.payload.pageSize;
    },
    setFilters: (state, action: PayloadAction<GetJoinNetworksTableFilters>) => {
      state.requestData.filters = { ...action.payload };
      state.requestData.paging = initialRequestData.paging;
    },
    setTab: (state, action: PayloadAction<string>) => {
      state.requestData.filters.tab = action.payload;
    },
    resetTableData: (state) => {
      state.data.tableData = initialState.data.tableData;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getJoinTable.pending, (state) => {
      state.data.tableDataIsLoading = true;
    });
    builder.addCase(getJoinTable.fulfilled, (state, action: PayloadAction<TableDataResponse<JoinNetworkTableView>>) => {
      state.data.tableDataIsLoading = false;
      state.data.tableData = action.payload;
      state.data.tableData.noResult = state.data.tableData.rows.length === 0;
    });
    builder.addCase(getJoinTable.rejected, (state) => {
      state.data.tableDataIsLoading = false;
    });

    builder.addCase(getJoinData.pending, (state) => {
      state.data.joinDataIsLoading = true;
    });
    builder.addCase(getJoinData.fulfilled, (state, action: PayloadAction<JoinNetworkData>) => {
      state.data.joinDataIsLoading = false;
      state.data.joinData = action.payload;
    });
    builder.addCase(getJoinData.rejected, (state) => {
      state.data.joinDataIsLoading = false;
    });

    builder.addCase(getJoinTableForLeadJobSeekerTabs.pending, (state) => {
      state.data.tabsIsLoading = true;
    });
    builder.addCase(getJoinTableForLeadJobSeekerTabs.fulfilled, (state, action: PayloadAction<Tab[]>) => {
      state.data.tabsIsLoading = false;
      state.data.tabs = action.payload;
    });
    builder.addCase(getJoinTableForLeadJobSeekerTabs.rejected, (state) => {
      state.data.tabsIsLoading = false;
    });
  }
});

export const { resetRequestData, setPage, setTab, setFilters, resetTableData } = networkJoinSlice.actions;
export const networkJoinReducer = networkJoinSlice.reducer;
